import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { AuthService, Roles } from '@core';
import { ProximityRightEnum } from '@models';
import { MenuItem } from 'primeng/api';
import { AppMenuitem } from './app.menuitem';

@Component({
  selector: '[app-menu]',
  standalone: true,
  imports: [CommonModule, AppMenuitem, RouterModule],
  template: `<ul class="layout-menu">
    <ng-container *ngFor="let item of model; let i = index">
      <li app-menuitem *ngIf="!item.separator && !item.spacer" [item]="item" [index]="i" [root]="true"></li>
      <li *ngIf="item.separator && item.visible !== false" class="menu-separator"></li>
      <li *ngIf="item.spacer && item.visible !== false" class="menu-section-gap"></li>
    </ng-container>
  </ul> `,
  styles: [
    `
      /* Pure vertical gap (no divider line) that pushes the dual-function
         "My Account" section well below the admin section above it.
         Rendered as its own <li>, not a .menu-separator, so no theme border
         rule applies to it. */
      .menu-section-gap {
        height: 4rem;
        list-style: none;
      }
    `,
  ],
})
export class AppMenu implements OnInit {
  constructor(private authService: AuthService) {}

  model: MenuItem[] = [];

  ngOnInit() {
    const user = this.authService.getUser();
    const R = ProximityRightEnum;

    const isAdmin = user?.hasRight(
      R.IsGlobalAdminForAccounts,
      R.IsGlobalAdminForSuppliers,
      R.CanManageSupplierAdministration,
      R.CanManageSupplierPartnerships,
      R.CanManageSupplierProducts,
      R.CanManageAccountAdministration,
      R.CanManageAccountOrganization,
      R.CanEditProgram,
      R.CanCreateProgram,
      R.CanApproveJobOrderByApprovalTree,
      R.CanEditUserByApprovalTree,
    );

    const canOrder = user?.hasRight(R.CanCreateJobOrders, R.CanEditJobOrderByApprovalTree);
    // "On behalf of Account Users" menu group: only admins who can actually edit orders
    // see these. Restricted admin variants (ApprovalAdmin has approve-only; SupportAdmin
    // has neither) hide them. Matches the landing page's "On Behalf" section rule.
    // Dual-role users still get self-service ordering via the "My Account" section below.
    const canActOnBehalf = user?.hasRight(R.CanEditJobOrderByApprovalTree);

    // A dual-function user is both an Account Admin and an Account User. For them
    // the menu splits into a labeled "Proximity Administration" section (their
    // unchanged admin items) and a "My Account" section (acting as themselves).
    const isDualRoleUser = !!user?.accountUserId && !!user?.isAccountAdmin;

    this.model = [
      {
        label: isDualRoleUser ? 'Proximity Administration' : '',
        items: [
          // --- Dashboard (admin only) ---
          {
            label: 'Dashboard',
            icon: 'fa-duotone fa-light fa-gauge',
            routerLink: ['/account-admin/dashboard'],
            visible: isAdmin,
          },
          { separator: true, visible: isAdmin },

          // --- Management (admin only) ---
          {
            label: 'Proximity Network',
            icon: 'fa-duotone fa-thin fa-globe',
            routerLink: ['/global'],
            visible: user?.hasRight(R.IsGlobalAdminForAccounts, R.IsGlobalAdminForSuppliers),
          },
          {
            label: 'Supplier Management',
            id: 'supplier-management',
            icon: 'fa-sharp-duotone fa-solid fa-truck-fast',
            routerLink: ['/supplier/suppliers'],
            visible: user?.hasRight(R.CanManageSupplierAdministration, R.CanManageSupplierPartnerships, R.CanManageSupplierProducts),
          },
          {
            label: 'Account Management',
            icon: 'fa-sharp-duotone fa-regular fa-chart-user',
            routerLink: ['/account/accounts'],
            visible: user?.hasRight(R.CanManageAccountAdministration, R.CanManageAccountOrganization, R.CanEditProgram, R.CanCreateProgram),
          },
          {
            label: 'Approval Management',
            icon: 'fa-sharp-duotone fa-solid fa-comments-question-check',
            routerLink: ['/account-admin/accounts'],
            visible: user?.hasRight(R.CanApproveJobOrderByApprovalTree, R.CanEditOwnData),
          },
          {
            // Principal Account Administrators and Global Administrators only — releasing a batch
            // dispatches every order in it to suppliers at once. Enforced by
            // collectionCanActivateGuard; this only decides whether the entry is drawn.
            //
            // A PAA has exactly one account so we route straight in. A Global Administrator has no
            // ambient accountId and gets COP's own account chooser, which knows to continue here
            // rather than to Approvals.
            label: 'Collect, Organize and Place',
            icon: 'fa-sharp-duotone fa-solid fa-inbox-full',
            routerLink: user?.accountId
              ? ['/account-admin/accounts', user.accountId, 'manage', 'collections']
              : ['/account-admin/collections'],
            visible: !!user?.isGlobalAdmin || !!user?.hasRole(Roles.PrincipalAccountAdmin),
          },
          {
            label: 'User Management',
            icon: 'fa-duotone fa-solid fa-users',
            routerLink: ['/account-admin/accounts-for-users'],
            visible: user?.hasRight(R.CanEditUserByApprovalTree, R.CanEditOwnData),
          },
          { separator: true, visible: isAdmin && (canActOnBehalf || (canOrder && !user?.isAccountAdmin)) },

          // --- Orders ---
          // For admins: these are the "on behalf of an account user" pages, gated
          // on CanEditJobOrderByApprovalTree so restricted variants (ApprovalAdmin,
          // SupportAdmin) don't see them.
          // For pure account users (not admins): these are their own self-service
          // pages, gated on canOrder.
          {
            label: 'Place an Order',
            icon: 'fa-sharp-duotone fa-solid fa-cart-shopping',
            routerLink: [user?.accountUserId && !user?.isAccountAdmin ? '/account-user/place-order-card' : '/account-user/place-order'],
            visible: user?.isAccountAdmin ? canActOnBehalf : canOrder,
          },
          {
            label: 'Order History',
            icon: 'fa-sharp-duotone fa-solid fa-clock-rotate-left',
            routerLink: ['/account-user/order-history'],
            title: 'Proximity Order History',
            visible: user?.isAccountAdmin ? canActOnBehalf : canOrder,
          },
          {
            label: 'Cancel an Item',
            icon: 'fa-sharp-duotone fa-solid fa-ban',
            routerLink: ['/account-user/cancellations'],
            visible: user?.isAccountAdmin ? canActOnBehalf : canOrder,
          },
          {
            label: 'Return an Item',
            icon: 'fa-sharp-duotone fa-solid fa-arrow-rotate-left',
            routerLink: ['/account-user/returns'],
            visible: user?.isAccountAdmin ? canActOnBehalf : canOrder,
          },
        ],
      },

      // Tall spacer (no divider line) to clearly set the "My Account" section
      // well below the admin section above (dual-function users only).
      // Rendered via the 'spacer' flag — see this component's template/styles.
      { spacer: true, visible: isDualRoleUser },

      // --- My Account (dual-function users only) ---
      // Routes the user, as themselves, to the existing personal account-user
      // pages with their own accountUserId. No admin behavior is affected.
      {
        label: 'My Account',
        visible: isDualRoleUser,
        items: [
          {
            label: 'Place My Order',
            icon: 'fa-sharp-duotone fa-solid fa-cart-shopping',
            routerLink: ['/account-user/place-order-card'],
            queryParams: { accountUserId: user?.accountUserId },
            title: 'Place an order from my own programs',
            visible: isDualRoleUser,
          },
          {
            label: 'My Order History',
            icon: 'fa-sharp-duotone fa-solid fa-clock-rotate-left',
            routerLink: ['/account-user/my-order-details', user?.accountId],
            queryParams: { accountUserId: user?.accountUserId },
            title: 'My personal order history',
            visible: isDualRoleUser,
          },
          {
            label: 'My Cancellations',
            icon: 'fa-sharp-duotone fa-solid fa-ban',
            routerLink: ['/account-user/accounts', user?.accountId, 'cancellation-details'],
            queryParams: { accountUserId: user?.accountUserId },
            title: 'My cancellations',
            visible: isDualRoleUser,
          },
          {
            label: 'My Returns',
            icon: 'fa-sharp-duotone fa-solid fa-arrow-rotate-left',
            routerLink: ['/account-user/accounts', user?.accountId, 'return-details'],
            queryParams: { accountUserId: user?.accountUserId },
            title: 'My returns',
            visible: isDualRoleUser,
          },
        ],
      },
    ];
  }
}
